import { computed } from 'vue'
import { getDisplayLocale } from '@bc-library/common'
import i18n from '@/config/translation'
import { useUserStore } from '@/config/store/userStore'

const officeCountryId = () => {
  const userStore = useUserStore()
  const currentJob = userStore.user?.currentJob
  if (!currentJob)
    return null
  return currentJob.office?.countryId
    || userStore.firmContext.offices.find(office => office._id === currentJob.officeId)?.countryId
    || null
}

/**
 * Locale of every date shown in the app: the language of the connected user with the date conventions of their
 * office's country (en-GB, en-US, fr-CH...). Pass it to `Intl.DateTimeFormat` and `toLocaleDateString` instead of
 * the i18n locale, which holds the language alone and makes English read as American (10/1/2026).
 * In a template, write `displayLocale` (unwrapped); in a script, `displayLocale.value`.
 */
export const displayLocale = computed(() => getDisplayLocale(i18n.global.locale.value, officeCountryId()))

type TWeekInfoLocale = Intl.Locale & { getWeekInfo?: () => { firstDay: number }, weekInfo?: { firstDay: number } }

/** First day of the week in the country of `displayLocale`, Intl convention: 1 = Monday ... 7 = Sunday (en-US, fr-CA). */
export const displayFirstDayOfWeek = computed(() => {
  const locale = new Intl.Locale(displayLocale.value) as TWeekInfoLocale
  return (locale.getWeekInfo?.() ?? locale.weekInfo)?.firstDay ?? 1
})

/**
 * `hour` option of a date + time format: 07:30 in 24-hour countries, 7:30 AM in 12-hour ones. A plain '2-digit'
 * reads 07:30 AM in the United States, a plain 'numeric' drops the zero elsewhere (7:30).
 */
export const displayHour = computed<'numeric' | '2-digit'>(() =>
  new Intl.DateTimeFormat(displayLocale.value, { hour: 'numeric' }).resolvedOptions().hourCycle?.startsWith('h1') ? 'numeric' : '2-digit')

/**
 * `locale` prop of v-calendar: a bare id would fall back on its own table ('en-FR' → 'en', American weeks starting on
 * Sunday), so the first day of the week is given explicitly (v-calendar: 1 = Sunday, 2 = Monday).
 */
export const vCalendarLocale = computed(() => ({ id: displayLocale.value, firstDayOfWeek: (displayFirstDayOfWeek.value % 7) + 1 }))

/** The twelve month names in the display language, for chart axes ("janvier"..., "January"...). */
export const displayMonthNames = (month: 'long' | 'short' | 'narrow' = 'long') => {
  const formatter = new Intl.DateTimeFormat(displayLocale.value, { month })
  return Array.from({ length: 12 }, (_, index) => formatter.format(new Date(2020, index, 1)))
}
